import React, { useState, useEffect, useCallback } from 'react';
import { X, RefreshCw, Play, Square, RotateCcw, Gamepad2, AlertTriangle, CheckCircle, XCircle, Loader2, Download } from 'lucide-react';

interface ServiceStatus {
  name: string;
  id: string;
  running: boolean;
  pid?: number;
  cpu?: number;
  mem?: number;
  uptime?: string;
}

interface ModStatus {
  loaded: boolean;
  lastSeen: string | null;
  error: string | null;
}

interface ZomboidServicesProps {
  isOpen: boolean;
  onClose: () => void;
  serverUrl: string;
}

const SERVICES = [
  { id: 'pz-server', name: 'PZ Game Server', desc: 'Project Zomboid dedicated server' },
  { id: 'pzrp-core', name: 'PZRP Core', desc: 'Rust RP backend (economy, factions, quests)' },
];

const ZomboidServices: React.FC<ZomboidServicesProps> = ({ isOpen, onClose, serverUrl }) => {
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [modStatus, setModStatus] = useState<ModStatus | null>(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, modRes] = await Promise.all([
        fetch(`${serverUrl}/api/zomboid/status`),
        fetch(`${serverUrl}/api/zomboid/mod-status`)
      ]);
      if (statusRes.ok) {
        const data = await statusRes.json();
        setServices(data.services || []);
      }
      if (modRes.ok) {
        const modData = await modRes.json();
        setModStatus(modData);
      }
    } catch (e) {
      setError('Failed to fetch status');
    }
    setLoading(false);
  }, [serverUrl]);

  const runAction = async (serviceId: string, action: 'start' | 'stop' | 'restart') => {
    setActionLoading(`${serviceId}-${action}`);
    setError(null);
    setLogs(prev => [...prev, `${action.toUpperCase()} ${serviceId}...`]);

    try {
      const res = await fetch(`${serverUrl}/api/zomboid/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ service: serviceId })
      });

      const data = await res.json();
      if (data.success) {
        setLogs(prev => [...prev, `✓ ${serviceId} ${action} successful`]);
        setTimeout(fetchStatus, 1000);
      } else {
        setLogs(prev => [...prev, `✗ ${serviceId} ${action} failed: ${data.error}`]);
        setError(data.error);
      }
    } catch (e) {
      setLogs(prev => [...prev, `✗ ${serviceId} ${action} failed`]);
      setError('Action failed');
    }
    setActionLoading(null);
  };

  const runAllAction = async (action: 'start' | 'stop' | 'restart') => {
    setActionLoading(`all-${action}`);
    setError(null);
    setLogs(prev => [...prev, `${action.toUpperCase()} ALL...`]);

    try {
      const res = await fetch(`${serverUrl}/api/zomboid/${action}-all`, {
        method: 'POST'
      });

      const data = await res.json();
      if (data.success) {
        setLogs(prev => [...prev, `✓ All services ${action} successful`]);
        setTimeout(fetchStatus, 2000);
      } else {
        setLogs(prev => [...prev, `✗ All services ${action} failed`]);
        setError(data.error);
      }
    } catch (e) {
      setError('Action failed');
    }
    setActionLoading(null);
  };

  useEffect(() => {
    if (isOpen) {
      fetchStatus();
      setLogs([]);
      const interval = setInterval(fetchStatus, 5000);
      return () => clearInterval(interval);
    }
  }, [isOpen, fetchStatus]);

  if (!isOpen) return null;

  const allRunning = services.length > 0 && services.every(s => s.running);
  const someRunning = services.some(s => s.running);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl w-[550px] max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <Gamepad2 className="w-5 h-5 text-green-400" />
            <span className="font-semibold text-white">Zomboid Services</span>
            <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
              allRunning ? 'bg-green-500/20 text-green-400' :
              someRunning ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-red-500/20 text-red-400'
            }`}>
              {allRunning ? 'All Running' : someRunning ? 'Partial' : 'Stopped'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchStatus}
              disabled={loading}
              className="p-1.5 hover:bg-gray-700 rounded transition-colors"
            >
              <RefreshCw className={`w-4 h-4 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-700 rounded transition-colors"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Mod Status */}
        {modStatus && (
          <div className={`px-4 py-2 border-b flex items-center gap-2 text-sm ${
            modStatus.loaded
              ? 'bg-green-900/20 border-green-800/50'
              : modStatus.error
                ? 'bg-red-900/20 border-red-800/50'
                : 'bg-gray-800/50 border-gray-700'
          }`}>
            {modStatus.loaded ? (
              <CheckCircle className="w-4 h-4 text-green-400" />
            ) : modStatus.error ? (
              <XCircle className="w-4 h-4 text-red-400" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-yellow-400" />
            )}
            <span className={modStatus.loaded ? 'text-green-400' : modStatus.error ? 'text-red-400' : 'text-yellow-400'}>
              MothershipRP:
            </span>
            <span className="text-gray-300 text-xs truncate flex-1">
              {modStatus.loaded ? 'Loaded' : modStatus.error || 'Unknown'}
            </span>
          </div>
        )}

        {/* Global Actions */}
        <div className="px-4 py-3 bg-gray-800/50 border-b border-gray-700 flex items-center justify-between">
          <span className="text-sm text-gray-400">All Services</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => runAllAction('start')}
              disabled={!!actionLoading || allRunning}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium bg-green-600/20 text-green-400 hover:bg-green-600/30 disabled:opacity-50 transition-colors"
            >
              {actionLoading === 'all-start' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
              Start All
            </button>
            <button
              onClick={() => runAllAction('stop')}
              disabled={!!actionLoading || !someRunning}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium bg-red-600/20 text-red-400 hover:bg-red-600/30 disabled:opacity-50 transition-colors"
            >
              {actionLoading === 'all-stop' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Square className="w-3.5 h-3.5" />}
              Stop All
            </button>
            <button
              onClick={() => runAllAction('restart')}
              disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium bg-yellow-600/20 text-yellow-400 hover:bg-yellow-600/30 disabled:opacity-50 transition-colors"
            >
              {actionLoading === 'all-restart' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
              Restart All
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="px-4 py-2 bg-red-900/30 border-b border-red-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-sm text-red-300">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Service List */}
        <div className="flex-1 overflow-auto p-4 space-y-3">
          {SERVICES.map((svc) => {
            const status = services.find(s => s.id === svc.id);
            const isRunning = status?.running || false;

            return (
              <div
                key={svc.id}
                className={`p-4 rounded-lg border ${
                  isRunning
                    ? 'bg-green-900/10 border-green-800/50'
                    : 'bg-gray-800/50 border-gray-700'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    {isRunning ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <XCircle className="w-5 h-5 text-gray-500" />
                    )}
                    <div>
                      <div className="font-medium text-white">{svc.name}</div>
                      <div className="text-xs text-gray-500">{svc.desc}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isRunning ? (
                      <button
                        onClick={() => runAction(svc.id, 'start')}
                        disabled={!!actionLoading}
                        className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-green-600/20 text-green-400 hover:bg-green-600/30 disabled:opacity-50"
                      >
                        {actionLoading === `${svc.id}-start` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                        Start
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => runAction(svc.id, 'restart')}
                          disabled={!!actionLoading}
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-yellow-600/20 text-yellow-400 hover:bg-yellow-600/30 disabled:opacity-50"
                        >
                          {actionLoading === `${svc.id}-restart` ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                          Restart
                        </button>
                        <button
                          onClick={() => runAction(svc.id, 'stop')}
                          disabled={!!actionLoading}
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-red-600/20 text-red-400 hover:bg-red-600/30 disabled:opacity-50"
                        >
                          {actionLoading === `${svc.id}-stop` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Square className="w-3 h-3" />}
                          Stop
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Stats when running */}
                {isRunning && status && (
                  <div className="flex items-center gap-4 mt-2 pt-2 border-t border-gray-700/50 text-xs text-gray-400">
                    <span>PID: <span className="text-gray-300 font-mono">{status.pid}</span></span>
                    {status.cpu !== undefined && (
                      <span>CPU: <span className={status.cpu > 50 ? 'text-yellow-400' : 'text-gray-300'}>{status.cpu.toFixed(1)}%</span></span>
                    )}
                    {status.mem !== undefined && (
                      <span>MEM: <span className={status.mem > 50 ? 'text-yellow-400' : 'text-gray-300'}>{status.mem.toFixed(1)}%</span></span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Action Log */}
        {logs.length > 0 && (
          <div className="border-t border-gray-700 px-4 py-2 max-h-24 overflow-auto bg-black/30">
            <div className="text-xs font-mono text-gray-500 space-y-0.5">
              {logs.slice(-5).map((log, i) => (
                <div key={i} className={log.startsWith('✓') ? 'text-green-400' : log.startsWith('✗') ? 'text-red-400' : 'text-gray-400'}>
                  {log}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-4 py-2 border-t border-gray-700 flex items-center justify-between">
          <span className="text-xs text-gray-500">Dev server: /home/forge/pz-server-dev</span>
          <a
            href={`${serverUrl}/api/zomboid/download-mod`}
            download="MothershipRP-client.tar.gz"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Download Mod
          </a>
        </div>
      </div>
    </div>
  );
};

export default ZomboidServices;
